
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FiFilter, FiGrid, FiShoppingBag, FiSearch, FiMenu, FiStar, FiChevronRight } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import Cart from '../components/Cart';
import { Helmet } from 'react-helmet-async';
import ProductCard from '../components/ProductCard';
import SkeletonCard from '../components/SkeletonCard';
import { useAuth } from '../context/AuthContext';
import { productAPI, categoryAPI } from '../services/api';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);

  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryBgHover = 'hover:bg-yellow-500';
  const primaryLight = 'bg-yellow-50';
  const primaryBorder = 'border-yellow-300';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-400';
  const secondaryBgHover = 'hover:bg-gray-500';
  const secondaryLight = 'bg-gray-100';
  const secondaryBorder = 'border-gray-300';

  // Charger les catégories au montage
  useEffect(() => {
    fetchCategories();
  }, []);

  // Lire les paramètres d'URL au chargement initial
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');
    
    if (categoryParam && categoryParam !== 'Toutes') {

      setSelectedCategory(categoryParam);
    } else if (searchParam && searchParam.trim().length >= 2) {
      setSearchTerm(searchParam);
    } else {
      setSelectedCategory('Toutes');
      setSearchTerm('');
    }
  }, []);

  // Mettre à jour l'URL quand la catégorie ou la recherche change
  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      // Mode recherche
      setSearchParams({ search: searchTerm });
    } else if (selectedCategory !== 'Toutes') {
      // Mode catégorie
      setSearchParams({ category: selectedCategory });
    } else {
      // Mode normal (tous les produits)
      setSearchParams({});
    }
  }, [selectedCategory, searchTerm, setSearchParams]);

  // Réinitialiser et recharger quand la catégorie ou la recherche change
  useEffect(() => {
    resetAndFetchProducts();
  }, [selectedCategory, searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategoriesList();
      if (response.success) {
        setCategories([{ id: 'all', name: 'Toutes' }, ...response.data]);
      }
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const resetAndFetchProducts = () => {
    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchProducts(1, true);
  };

  const fetchProducts = async (page = 1, isReset = false) => {
    try {
      if (isReset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      let response;
      
      // Si c'est une recherche
      if (searchTerm.trim().length >= 2) {
        response = await productAPI.searchProductsAdvanced(searchTerm, page, 50);
      } else {
        // Sinon, récupérer par catégorie (ou tous)
        let categoryId = 'all';
        
        if (selectedCategory !== 'Toutes') {
            // Récupérer l'ID de la catégorie à partir de son nom
            try {
                const categoryResponse = await categoryAPI.getCategoryIdByName(selectedCategory);
                if (categoryResponse.success && categoryResponse.data) {
                    categoryId = categoryResponse.data.id;
                } else {
                    // Si la catégorie n'est pas trouvée, on utilise 'all'
                    categoryId = 'all';
                }
            } catch (error) {
                console.error('Erreur lors de la récupération de l\'ID de la catégorie:', error);
                categoryId = 'all';
            }
        }
        
        response = await productAPI.getProductsOptimizedByCategory(categoryId, page, 50);
      }
      
      if (response.success) {
        const newProducts = response.data;
        const meta = response.meta;
        
        if (isReset) {
          setProducts(newProducts);
        } else {
          setProducts(prev => [...prev, ...newProducts]);
        }
        
        setHasMore(meta?.has_more_pages ?? false);
        setTotalProducts(meta?.total ?? 0);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore && !loading) {
      await fetchProducts(currentPage + 1, false);
    }
  }, [currentPage, hasMore, loadingMore, loading]);

  // Hook pour l'infinite scroll
  // const { lastElementRef, isFetching } = useInfiniteScroll(loadMore, hasMore && !loading);
  const { lastElementRef, isFetching } = useInfiniteScroll(
    loadMore, 
    hasMore, 
    loading || loadingMore  // ← Ajoutez ce paramètre
  );

  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
    setSearchTerm('');
    setShowMobileFilters(false);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim().length >= 2) {
      setSelectedCategory('Toutes');
    }
  };

  // Filtrer les produits (garde la logique existante)
  const filteredProducts = products; // Pas de filtrage supplémentaire

  // Mettre à jour le titre de la page en fonction du contexte
  const getPageTitle = () => {
    if (searchTerm.trim().length >= 2) {
      return `Recherche : ${searchTerm}`;
    } else if (selectedCategory !== 'Toutes') {
      return selectedCategory;
    }
    return 'Tous les produits';
  };

  return (
    <>

      {/* Balises SEO pour la page d'accueil */}
      <Helmet>
        <title>StelonneMarket - {getPageTitle()} | Votre boutique en ligne préférée</title>
        <meta 
          name="description" 
          content={`Découvrez nos ${getPageTitle()} de qualité sur StelonneMarket. Meilleures offres, livraison rapide et service client exceptionnel.`} 
        />
        <meta 
          name="keywords" 
          content={`boutique en ligne, e-commerce, ${getPageTitle()}, produits, StelonneMarket`} 
        />
        
        {/* Open Graph */}
        <meta property="og:title" content={`StelonneMarket - ${getPageTitle()}`} />
        <meta property="og:image" content="https://stelonne-market.online/public/logo192.png" />
      </Helmet>


      <div className="min-h-screen bg-gray-50">
        <Navbar 
          showAdminControls={isAdmin} 
          onBackToDashboard={handleBackToDashboard}
        />
        <Cart />

        {/* Hero Section avec nouvelle charte */}
        <div className="bg-gradient-to-r from-gray-700 via-gray-500 to-gray-700 text-white py-8 md:py-12 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-400 rounded-full translate-y-24 -translate-x-24"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 tracking-tight">
                Bienvenue chez <span className="text-yellow-400">StelonneMarket</span>
              </h1>
              <div className="inline-flex items-center gap-2 mb-4">
                <FiStar className="text-yellow-400 text-2xl" />
                <span className="text-yellow-400 font-bold text-sm tracking-wide">Decouvrez - Achetez - Profitez</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filter Button - Nouveau design */}
        <div className="md:hidden sticky top-16 bg-white shadow-sm z-40 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-500 to-gray-500 text-white rounded-xl font-semibold shadow-md"
            >
              <div className="flex items-center gap-3">
                <FiFilter className="text-lg text-yellow-400" />
                <span>Filtres & Recherche</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-bold rounded-full ${primaryBg}`}>
                  {selectedCategory === 'Toutes' ? categories.length - 1 : 1}
                </span>
                <FiChevronRight className={`text-lg transition-transform ${showMobileFilters ? 'rotate-90' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Categories Filter & Search - Desktop avec nouvelle charte */}
        <div className="hidden md:block sticky top-16 bg-white shadow-md z-30 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <div className={`p-2 ${primaryLight} rounded-lg`}>
                  <FiFilter className={`text-lg ${primaryColor}`} />
                </div>
                <h2 className="font-bold text-gray-900">Catégories</h2>
              </div>
              
              <div className="flex-1 relative max-w-md ml-auto">
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Catégories Desktop - Nouveau design */}
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {categories.map((category) => (
                <button
                  key={category.id || category.name}
                  onClick={() => handleCategoryChange(category.name)}
                  className={`px-5 py-2.5 rounded-xl whitespace-nowrap font-semibold transition-all duration-200 border ${
                    selectedCategory === category.name
                      ? `${primaryBg} text-white shadow-lg border-yellow-400`
                      : `${secondaryLight} text-gray-700 border-gray-200 hover:border-gray-300 ${secondaryBgHover}`
                  }`}
                >
                  {category.name}
                  {selectedCategory === category.name && (
                    <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Filters Overlay avec nouvelle charte */}
        {showMobileFilters && (
          <div className="md:hidden fixed inset-0 bg-black bg-opacity-60 z-50 backdrop-blur-sm">
            <div className="absolute right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl">
              <div className="p-4 sm:p-6 h-full flex flex-col">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Filtres & Recherche</h3>
                    <p className="text-sm text-gray-500 mt-1">Affinez votre sélection</p>
                  </div>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                  >
                    <FiMenu className="text-xl" />
                  </button>
                </div>

                {/* Recherche Mobile */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Rechercher un produit
                  </label>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Nom du produit..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-gray-50"
                    />
                  </div>
                </div>

                {/* Catégories Mobile */}
                <div className="flex-1 overflow-y-auto">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Catégories
                  </label>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category.id || category.name}
                        onClick={() => handleCategoryChange(category.name)}
                        className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-all duration-200 border ${
                          selectedCategory === category.name
                            ? `${primaryBg} text-white border-yellow-400 shadow-md`
                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{category.name}</span>
                          {selectedCategory === category.name && (
                            <span className="text-lg">✓</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full py-3 bg-gradient-to-r from-gray-400 to-gray-400 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    Appliquer les filtres
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid avec nouveau design */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-6 md:py-8">
          {/* Header produits */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${primaryLight} rounded-lg`}>
                <FiGrid className={`text-xl ${primaryColor}`} />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {getPageTitle()}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {totalProducts > 0 && `${totalProducts} produits disponibles`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className={`px-3 py-1.5 ${primaryLight} rounded-lg`}>
                {/* <span className="text-sm font-semibold text-gray-800">
                  {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''}
                </span> */}
              </div>
              <button
                onClick={() => setShowMobileFilters(true)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <FiFilter className="text-lg" />
              </button>
            </div>
          </div>

          {/* Loading State Initial */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(20)].map((_, index) => (
                <SkeletonCard key={`skeleton-${index}`} />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            /* Empty State */
            <div className="text-center py-12 md:py-16 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                <FiShoppingBag className="text-3xl text-gray-400" />
              </div>
              <p className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                Aucun produit trouvé
              </p>
              <p className="text-gray-500 max-w-md mx-auto">
                Essayez de changer de catégorie ou de modifier vos critères de recherche
              </p>
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => {
                    setSelectedCategory('Toutes');
                    setSearchTerm('');
                    navigate('/');
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Réinitialiser les filtres
                </button>
                <button
                  onClick={() => setShowMobileFilters(true)}
                  className="px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all"
                >
                  Modifier les filtres
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Products Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredProducts.map((product, index) => {
                  // Attacher la ref au dernier élément pour déclencher le chargement
                  // if (index === filteredProducts.length - 1) {
                  //   return (
                  //     <div ref={lastElementRef} key={product.id}>
                  //       <ProductCard 
                  //         product={product}
                  //         showSupplierInfo={isAdmin}
                  //       />
                  //     </div>
                  //   );
                  // }
                  // return (
                  //   <ProductCard 
                  //     key={product.id} 
                  //     product={product}
                  //     showSupplierInfo={isAdmin}
                  //   />
                  // );
                  const isLastElement = index === filteredProducts.length - 1;
        
                  return (
                      <div 
                          key={product.id} 
                          ref={isLastElement ? lastElementRef : null}
                      >
                          <ProductCard 
                              product={product}
                              showSupplierInfo={isAdmin}
                          />
                      </div>
                  );
                })}
              </div>
              {/* Sentinelle de chargement supplémentaire (optionnel mais recommandé) */}
              {hasMore && !loading && filteredProducts.length > 0 && (
                  <div 
                      ref={lastElementRef}  // ← Ref aussi sur la sentinelle
                      className="w-full flex justify-center items-center py-8"
                  >
                      {loadingMore && (
                          <div className="flex justify-center items-center gap-3">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                              <span className="text-gray-500">Chargement de plus de produits...</span>
                          </div>
                      )}
                      {!loadingMore && hasMore && (
                          <div className="h-16 w-full flex justify-center items-center text-gray-400">
                              <span>↓ Scrollez pour charger plus ↓</span>
                          </div>
                      )}
                  </div>
              )}
              
              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                  <span className="ml-2 text-gray-500">Chargement de plus de produits...</span>
                </div>
              )}
              
              {/* Fin de la liste */}
              {!hasMore && filteredProducts.length > 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p> Vous avez vu tous nos produits !</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Mobile Category Indicator avec nouveau design */}
        <div className="md:hidden fixed bottom-4 left-4 right-4">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 transform hover:scale-[1.02] transition-transform duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Catégorie active</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${primaryBg}`}></div>
                  <p className="font-bold text-gray-900">{selectedCategory}</p>
                </div>
              </div>
              <button
                onClick={() => setShowMobileFilters(true)}
                className="px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg"
              >
                Modifier
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;